import React, {useCallback, useEffect, useRef, useState} from 'react'
import {toast} from 'react-toastify'
import {LoadingData} from '../../../../components/LoadingData'
import {ModeleSavModeleMessage, SavModelesResponse} from '../../../../components/sav/Models'
import {
  apercuSavModeleMessage,
  createSavModeleMessage,
  getSavModelesMessages,
  savErreur,
  updateSavModeleMessage,
} from '../../../../components/sav/requests'
import {formatSavDate} from '../../../../components/sav/libelles'

type Edition = {
  id?: number
  evenement: number | ''
  canal: 'MAIL' | 'SMS'
  entite: number | ''
  libelle: string
  objet: string
  corps: string
  actif: boolean
  texteParDefaut: boolean
}

const versEdition = (m: ModeleSavModeleMessage): Edition => ({
  id: m.id,
  evenement: m.evenement.id,
  canal: m.canal,
  entite: m.entite?.id ?? '',
  libelle: m.libelle,
  objet: m.objet ?? '',
  corps: m.corps,
  actif: m.actif,
  texteParDefaut: m.texteParDefaut,
})

const NOUVEAU: Edition = {
  evenement: '',
  canal: 'MAIL',
  entite: '',
  libelle: '',
  objet: '',
  corps: '',
  actif: true,
  texteParDefaut: false,
}

// Écran 9 : Paramètres › Messages SAV
const SavMessagesPage = () => {
  const [donnees, setDonnees] = useState<SavModelesResponse | null>(null)
  const [edition, setEdition] = useState<Edition | null>(null)
  const [dossierApercu, setDossierApercu] = useState('')
  const [apercu, setApercu] = useState<{
    objet: string
    corps: string
    variablesVides: Array<string>
  } | null>(null)
  const [envoi, setEnvoi] = useState(false)
  const champCorps = useRef<HTMLTextAreaElement>(null)

  const charger = useCallback(
    (selection?: number) =>
      getSavModelesMessages()
        .then((d) => {
          setDonnees(d)
          const cible = d.modeles.find((m) => m.id === selection) ?? d.modeles[0]
          if (cible && selection !== -1) setEdition(versEdition(cible))
        })
        .catch((e) => toast.error(savErreur(e))),
    []
  )

  useEffect(() => {
    charger()
  }, [charger])

  if (!donnees) return <LoadingData />

  const maj = (changement: Partial<Edition>) => setEdition((e) => (e ? {...e, ...changement} : e))
  const inconnues = edition
    ? Array.from(new Set(`${edition.objet} ${edition.corps}`.match(/\{([a-z_]+)\}/g) ?? [])).filter(
        (v) => !donnees.variables.includes(v.slice(1, -1))
      )
    : []

  const inserer = (variable: string) => {
    if (!edition) return
    maj({corps: `${edition.corps}{${variable}}`})
    champCorps.current?.focus()
  }

  const enregistrer = () => {
    if (!edition) return
    setEnvoi(true)
    const champs = {
      libelle: edition.libelle,
      objet: edition.canal === 'MAIL' ? edition.objet : null,
      corps: edition.corps,
      actif: edition.actif,
    }
    const appel = edition.id
      ? updateSavModeleMessage(edition.id, champs)
      : createSavModeleMessage({
          ...champs,
          evenement: edition.evenement,
          canal: edition.canal,
          entite: edition.entite || null,
        })
    appel
      .then((m) => {
        toast.success('Modèle enregistré')
        charger(m.id)
      })
      .catch((e) => toast.error(savErreur(e)))
      .finally(() => setEnvoi(false))
  }

  const previsualiser = () => {
    if (!edition || !dossierApercu.trim()) return
    apercuSavModeleMessage(dossierApercu.trim(), edition.objet, edition.corps)
      .then(setApercu)
      .catch((e) => toast.error(savErreur(e)))
  }

  const sms = edition?.canal === 'SMS'

  return (
    <div className='row g-5'>
      <div className='col-lg-7'>
        <div className='card'>
          <div className='card-header'>
            <h3 className='card-title'>Modèles de messages SAV</h3>
            <div className='card-toolbar'>
              <button
                className='btn btn-sm btn-success'
                onClick={() => {
                  setEdition({...NOUVEAU})
                  setApercu(null)
                }}
              >
                + Nouveau modèle
              </button>
            </div>
          </div>
          <div className='card-body'>
            <div className='text-muted mb-4'>
              Pour chaque événement et chaque canal : un modèle commun à tout le groupe et, si
              besoin, une variante par entité, qui le remplace à l'envoi pour les dossiers de cette
              entité. Seuls les modèles Mail sont envoyés en V1. Les textes actuels sont des textes
              par défaut, en attente du métier.
            </div>
            <div className='table-responsive'>
              <table className='table align-middle gs-0 gy-2'>
                <thead>
                  <tr className='fw-bold text-muted'>
                    <th>Événement</th>
                    <th>Canal</th>
                    <th>Entité</th>
                    <th>Libellé</th>
                    <th>État</th>
                    <th>Modifié le</th>
                  </tr>
                </thead>
                <tbody>
                  {donnees.modeles.map((m) => {
                    const actif = edition?.id === m.id
                    return (
                      <tr
                        key={m.id}
                        className='cursor-pointer'
                        style={{
                          backgroundColor: actif ? '#FFFDF0' : undefined,
                          boxShadow: actif ? 'inset 4px 0 0 #50CD89' : undefined,
                        }}
                        onClick={() => {
                          setEdition(versEdition(m))
                          setApercu(null)
                        }}
                      >
                        <td className='ps-3'>{m.evenement.libelle}</td>
                        <td>
                          {m.canal === 'MAIL' ? (
                            <span className='badge badge-light-primary'>Mail</span>
                          ) : (
                            <span
                              className='badge'
                              style={{backgroundColor: '#F3E8FF', color: '#6B21A8'}}
                            >
                              SMS · V2
                            </span>
                          )}
                        </td>
                        <td>{m.entite?.libelle ?? 'Toutes (défaut)'}</td>
                        <td>{m.libelle}</td>
                        <td>
                          {m.actif ? (
                            <span className='badge badge-light-success'>Actif</span>
                          ) : (
                            <span className='badge badge-light'>Inactif</span>
                          )}
                        </td>
                        <td className='text-muted fs-7'>{formatSavDate(m.dateModification)}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <div className='col-lg-5'>
        {edition && (
          <div className='card' style={{position: 'sticky', top: 90}}>
            <div className='card-header'>
              <h3 className='card-title'>{edition.id ? 'Modifier le modèle' : 'Nouveau modèle'}</h3>
              <div className='card-toolbar'>
                {sms ? (
                  <span className='badge' style={{backgroundColor: '#F3E8FF', color: '#6B21A8'}}>
                    SMS · V2
                  </span>
                ) : (
                  <span className='badge badge-light-primary'>Mail</span>
                )}
              </div>
            </div>
            <div className='card-body'>
              {sms && (
                <div className='alert alert-info py-2'>
                  Envoi SMS prévu en V2 : ce modèle peut être préparé, mais il n'est pas proposé à
                  l'envoi tant que le service SMS n'existe pas.
                </div>
              )}
              {edition.id ? (
                <div className='mb-3'>
                  <div>
                    <span className='text-muted'>Événement : </span>
                    {donnees.evenements.find((e) => e.id === edition.evenement)?.libelle}
                  </div>
                  <div>
                    <span className='text-muted'>Entité : </span>
                    {donnees.entites.find((e) => e.id === edition.entite)?.libelle ??
                      'Toutes (défaut)'}
                  </div>
                  {edition.texteParDefaut && (
                    <div className='badge badge-light-warning mt-2'>
                      Texte par défaut, en attente du métier
                    </div>
                  )}
                </div>
              ) : (
                <div className='row g-3 mb-3'>
                  <div className='col-md-6'>
                    <label className='form-label required'>Événement</label>
                    <select
                      className='form-select'
                      value={edition.evenement}
                      onChange={(e) =>
                        maj({evenement: e.target.value ? Number(e.target.value) : ''})
                      }
                    >
                      <option value=''>Choisir</option>
                      {donnees.evenements.map((e) => (
                        <option key={e.id} value={e.id}>
                          {e.libelle}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className='col-md-3'>
                    <label className='form-label'>Canal</label>
                    <select
                      className='form-select'
                      value={edition.canal}
                      onChange={(e) => maj({canal: e.target.value as 'MAIL' | 'SMS'})}
                    >
                      <option value='MAIL'>Mail</option>
                      <option value='SMS'>SMS (V2)</option>
                    </select>
                  </div>
                  <div className='col-md-3'>
                    <label className='form-label'>Entité</label>
                    <select
                      className='form-select'
                      value={edition.entite}
                      onChange={(e) => maj({entite: e.target.value ? Number(e.target.value) : ''})}
                    >
                      <option value=''>Toutes</option>
                      {donnees.entites.map((e) => (
                        <option key={e.id} value={e.id}>
                          {e.libelle}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              <label className='form-label required'>Libellé</label>
              <input
                className='form-control form-control-solid mb-3'
                value={edition.libelle}
                onChange={(e) => maj({libelle: e.target.value})}
              />
              {!sms && (
                <>
                  <label className='form-label required'>Objet du mail</label>
                  <input
                    className='form-control form-control-solid mb-3'
                    value={edition.objet}
                    onChange={(e) => maj({objet: e.target.value})}
                  />
                </>
              )}
              <label className='form-label required'>Texte du message</label>
              <textarea
                ref={champCorps}
                className='form-control form-control-solid'
                rows={8}
                value={edition.corps}
                onChange={(e) => maj({corps: e.target.value})}
              />
              <div
                className={`text-end fs-7 mb-3 ${
                  sms && edition.corps.length > 160 ? 'text-danger fw-bold' : 'text-muted'
                }`}
              >
                {edition.corps.length} caractère(s){sms && ' / 160'}
              </div>
              <div className='mb-4'>
                <div className='text-muted fs-7 mb-1'>Insérer une variable</div>
                <div className='d-flex flex-wrap gap-1'>
                  {donnees.variables.map((v) => (
                    <button
                      key={v}
                      type='button'
                      className='btn btn-sm btn-light-success py-1 px-2'
                      onClick={() => inserer(v)}
                    >
                      {`{${v}}`}
                    </button>
                  ))}
                </div>
              </div>
              {inconnues.length > 0 && (
                <div className='alert alert-danger py-2'>
                  Variable(s) inconnue(s) : {inconnues.join(', ')}
                </div>
              )}

              <label className='form-label'>Aperçu sur un dossier réel</label>
              <div className='input-group mb-3'>
                <input
                  className='form-control'
                  placeholder='N° SAV (ex. SAV-2026-000148)'
                  value={dossierApercu}
                  onChange={(e) => setDossierApercu(e.target.value)}
                />
                <button
                  className='btn btn-light-primary'
                  onClick={previsualiser}
                  disabled={!dossierApercu.trim()}
                >
                  Aperçu
                </button>
              </div>
              {apercu && (
                <div className='bg-light rounded p-4 mb-4'>
                  {!sms && <div className='fw-bold mb-2'>{apercu.objet}</div>}
                  <div style={{whiteSpace: 'pre-line'}}>{apercu.corps}</div>
                  {apercu.variablesVides.length > 0 && (
                    <div className='text-warning fs-7 mt-2'>
                      Vide(s) sur ce dossier : {apercu.variablesVides.join(', ')} (l'envoi serait
                      bloqué).
                    </div>
                  )}
                </div>
              )}

              <label className='form-check form-check-custom form-check-solid mb-5'>
                <input
                  className='form-check-input'
                  type='checkbox'
                  checked={edition.actif}
                  onChange={(e) => maj({actif: e.target.checked})}
                />
                <span className='form-check-label'>Modèle actif</span>
              </label>
              <div className='d-flex justify-content-end gap-3'>
                <button
                  className='btn btn-light'
                  onClick={() => (edition.id ? charger(edition.id) : setEdition(null))}
                  disabled={envoi}
                >
                  Annuler
                </button>
                <button
                  className='btn btn-success'
                  onClick={enregistrer}
                  disabled={envoi || inconnues.length > 0}
                >
                  Enregistrer le modèle
                </button>
              </div>
              <div className='text-muted fs-8 mt-3'>
                Une variable inconnue bloque l'enregistrement. Chaque modification est tracée
                (auteur, date).
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default SavMessagesPage
